import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

import { AI_WORKSHOP_ROW_1, AI_WORKSHOP_ROW_2 } from './aiWorkshopDemoData';
import AiWorkshopCard from './aiWorkshopCard';

const AUTO_SCROLL_INTERVAL = 3000;

// Per Figma mobile spec: every card is 320x352.5(ish), just with a slightly
// different corner radius per deck.
const MOBILE_CARDS = [
  ...AI_WORKSHOP_ROW_2.map((card) => ({
    ...card,
    illustration: card.mobileIllustration,
    width: 320.43,
    height: 352.97,
    radius: 19.39,
  })),
  ...AI_WORKSHOP_ROW_1.map((card) => ({
    ...card,
    illustration: card.mobileIllustration,
    width: 320,
    height: 352.5,
    radius: 16.67,
  })),
];

const AiWorkshopDemo = () => {
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % MOBILE_CARDS.length);
    }, AUTO_SCROLL_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const card = cardRefs.current[activeIndex];
    if (container && card) {
      container.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }
  }, [activeIndex]);

  return (
    <div className="flex flex-col items-center bg-white sm:py-20 sm:px-[120px] py-[60px] px-5 gap-10">
      <div className="flex flex-col items-center gap-1 w-full max-w-[1200px]">
        <motion.div
          className="w-full text-[24px] leading-[30px] sm:text-[40px] sm:leading-[52px] tracking-[0.02em] sm:tracking-[0.04em] text-center text-[#020617] font-gilroy-bold"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          AI Projects that you'll build
        </motion.div>
        <motion.div
          className="w-full sm:hidden text-[14px] font-normal leading-[18px] tracking-[0.02em] text-center text-[#020617] font-[Gilroy-Regular]"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
        >
          while building with and preparing for high-growth technical roles
        </motion.div>
        <motion.div
          className="w-full hidden sm:block text-[16px] leading-[22px] tracking-[0.02em] text-center text-[#020617] font-[Gilroy-Regular]"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
        >
          A complete learning system to help you master software engineering, build with AI and prepare for high-growth technical roles.
        </motion.div>
      </div>
      <div className="flex flex-col gap-6 w-full max-w-[1200px]">
        {/* Mobile: every card (both rows) in one horizontally scrollable row */}
        <div
          ref={scrollContainerRef}
          className="no-scrollbar flex sm:hidden gap-3 w-full overflow-x-auto snap-x snap-mandatory"
        >
          {MOBILE_CARDS.map((card, index) => (
            <div
              key={card.illustration}
              ref={(el) => (cardRefs.current[index] = el)}
              className="shrink-0 snap-start"
              style={{ width: card.width, height: card.height }}
            >
              <AiWorkshopCard {...card} radius={card.radius} />
            </div>
          ))}
        </div>

        {/* Desktop: original two-tier layout */}
        <div className="hidden sm:flex flex-row gap-6 w-full">
          {AI_WORKSHOP_ROW_1.map((card) => (
            <div key={card.illustration} className="flex-1 min-w-0 h-[320px]">
              <AiWorkshopCard {...card} />
            </div>
          ))}
        </div>
        <div className="hidden sm:grid sm:grid-cols-3 gap-6 w-full">
          {AI_WORKSHOP_ROW_2.map((card) => (
            <div key={card.illustration} className="min-w-0 h-[423px]">
              <AiWorkshopCard {...card} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AiWorkshopDemo;
